import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { DataType, GUID, Note, NoteType, UnencryptedData } from '@ndvr/portfolio-api';

import { PortfolioApi } from 'API';
import { useAuthContext } from 'views/auth/Auth';
import { Toast } from 'widgets';

export const useNote = (entityGUID: GUID<any>, noteType: NoteType) =>
    useQuery({
        queryKey: ['note', 'entity', entityGUID, 'noteType', noteType],
        queryFn: async () => {
            const [response] = await PortfolioApi().getNote(entityGUID, { noteType });
            return response ?? undefined;
        },
    });

export const useCreateNote = () => {
    const queryClient = useQueryClient();
    const { user } = useAuthContext();

    return useMutation({
        mutationFn: ({
            entityGUID,
            entityType,
            noteType = NoteType.GENERAL,
            content,
        }: {
            entityGUID: GUID<any>;
            entityType: string;
            noteType: NoteType;
            content: string;
        }) => {
            return PortfolioApi().createNote({
                entityGUID,
                entityType,
                noteType,
                content: {
                    encryptionContextGUID: user!.personOrgGUID,
                    dataType: DataType.NOTE_CONTENT,
                    plainText: content,
                },
            });
        },
        onSuccess: (_, { entityGUID, noteType }) => {
            queryClient.refetchQueries({
                queryKey: ['note', 'entity', entityGUID, 'noteType', noteType],
            });
        },
        onError: (error) => Toast.forError(error, 'Failed create note'),
    });
};

export const useUpdateNote = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({
            noteGUID,
            content,
        }: {
            noteGUID: GUID<Note>;
            entityGUID: GUID<any>;
            noteType: NoteType;
            content: UnencryptedData;
        }) => {
            return PortfolioApi().updateNote(noteGUID, content);
        },
        onSuccess: (_, { entityGUID, noteType }) => {
            queryClient.invalidateQueries({
                queryKey: ['note', 'entity', entityGUID, 'noteType', noteType],
            });
        },
        onError: (error) => Toast.forError(error, 'Failed update note'),
    });
};
